<template>
  <div class="space-y-2 relative">
    <!-- Search-as-you-type input -->
    <div>
      <div class="flex items-center justify-between mb-1">
        <div class="flex items-center gap-2 flex-1">
          <span class="text-xs text-gray-500 dark:text-gray-400">
            {{ props.mapping?.knxDpt }}
          </span>
        </div>
      </div>
      <div class="relative">
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          :placeholder="tt('Type to search (e.g., 1/1/1)', 'Vnesite za iskanje (npr. 1/1/1)')"
          class="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          @input="onSearchInput"
          @focus="onSearchFocus"
          @blur="onSearchBlur"
          @keydown.enter.prevent="selectFirstMatch"
          @keydown.escape="closeDropdown"
          @keydown.arrow-down.prevent="navigateDown"
          @keydown.arrow-up.prevent="navigateUp" />
        <span
          v-if="loading"
          class="absolute right-2 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400 animate-spin">
          progress_activity
        </span>
        <span
          v-else-if="searchQuery && !showDropdown"
          class="absolute right-2 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400">
          search
        </span>
      </div>
    </div>

    <!-- Dropdown results -->
    <div
      v-if="showDropdown && filtered.length > 0"
      class="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl">
      <div
        v-if="error"
        class="px-3 py-2 text-xs text-red-600 dark:text-red-400 border-b border-gray-200 dark:border-gray-700">
        {{ error }}
      </div>
      <ul class="py-1">
        <li
          v-for="(addr, index) in filtered"
          :key="addr.knx_address_id || addr.groupAddress"
          :class="[
            'px-3 py-2 cursor-pointer transition-colors',
            highlightedIndex === index ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-700',
          ]"
          @mousedown.prevent="selectAddress(addr)"
          @mouseenter="highlightedIndex = index">
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <span class="font-medium text-sm text-gray-900 dark:text-white">
                  {{ addr.groupAddress }}<span v-if="addr.addressName"> - {{ addr.addressName }}</span>
                </span>
              </div>
              <div class="flex items-center gap-2 mt-1">
                <span
                  v-if="isCompatibleDpt(getAddressDptValue(addr))"
                  class="px-1.5 py-0.5 text-xs rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                  {{ getAddressDptValue(addr) ?? '-' }}
                </span>
                <span
                  v-else
                  class="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                  {{ getAddressDptValue(addr) ?? '-' }}
                </span>
                <span
                  v-if="getDptDescription(extractDptNumber(getAddressDptValue(addr)))"
                  class="text-xs text-gray-500 dark:text-gray-500">
                  {{ getDptDescription(extractDptNumber(getAddressDptValue(addr))) }}
                </span>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <!-- Empty state -->
    <div
      v-if="showDropdown && filtered.length === 0 && searchQuery && !loading"
      class="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl p-3">
      <div class="text-xs text-gray-500 dark:text-gray-400 text-center">
        {{ tt('No addresses found for', 'Ni najdenih naslovov za') }} "{{ searchQuery }}"
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue';
import type { PropertyMapping } from '../shared/propertyMapping';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';

export interface KNXConfig {
  groupAddress: string;
  dpt: number;
}

/** One row of the hub's KNX datapoint list — the shape this picker reads. */
interface KnxAddressRow {
  knx_address_id?: string | number;
  groupAddress?: string | number;
  addressName?: string;
  dpt?: string | number;
  dptType?: string | number;
}

interface Props {
  modelValue?: KNXConfig;
  mapping: PropertyMapping | undefined;
  integrationId: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: KNXConfig];
}>();

function normalizeDptInput(dpt: unknown): unknown {
  if (Array.isArray(dpt) && dpt.length > 0) {
    return dpt[0];
  }
  return dpt;
}

function extractDptNumber(dpt: unknown): number {
  const normalized = normalizeDptInput(dpt);
  if (typeof normalized === 'number') return normalized;
  if (!normalized) return 1;

  const match = String(normalized).match(/\d+/);
  return match ? parseInt(match[0]) : 1;
}

function getAddressDptValue(addr: KnxAddressRow): string | number | undefined {
  if (!addr) return undefined;
  return addr.dpt ?? addr.dptType;
}

function getExpectedDptNumber(): number | undefined {
  if (props.mapping?.knxDpt) {
    return extractDptNumber(props.mapping.knxDpt);
  }

  if (localConfig.value.dpt) {
    return localConfig.value.dpt;
  }

  return undefined;
}

// Search state - declared first so watchers can use them
const searchInput = ref<HTMLInputElement>();
const searchQuery = ref('');
const showDropdown = ref(false);
const loading = ref(false);
const error = ref<string | null>(null);
const addresses = ref<KnxAddressRow[]>([]);
const highlightedIndex = ref(0);

const localConfig = ref<KNXConfig>({
  groupAddress: props.modelValue?.groupAddress || '',
  dpt: props.modelValue?.dpt || extractDptNumber(props.mapping?.knxDpt),
});

// Initialize searchQuery with proper value after localConfig is set
searchQuery.value = localConfig.value.groupAddress || '';

console.log('[KNXReferenceInput] Initialized:', {
  modelValue: props.modelValue,
  localConfig: localConfig.value,
  mapping: props.mapping,
  extractedDpt: extractDptNumber(props.mapping?.knxDpt),
});

// DPT type descriptions (common types)
const DPT_DESCRIPTIONS: Record<number, [string, string]> = {
  1: ['Boolean (On/Off, Switch)', 'Logična vrednost (vklop/izklop, stikalo)'],
  2: ['Boolean Controlled', 'Krmiljena logična vrednost'],
  3: ['Dimming Control (3-bit)', 'Krmiljenje zatemnitve (3-bitno)'],
  4: ['Character (ASCII)', 'Znak (ASCII)'],
  5: ['Unsigned Int (0-255, Percentage)', 'Nepredznačeno celo število (0-255, odstotek)'],
  6: ['Signed Int (-128 to 127)', 'Predznačeno celo število (-128 do 127)'],
  7: ['Unsigned Int (0-65535)', 'Nepredznačeno celo število (0-65535)'],
  8: ['Signed Int (-32768 to 32767)', 'Predznačeno celo število (-32768 do 32767)'],
  9: ['Float (Temperature, Brightness)', 'Decimalno število (temperatura, svetlost)'],
  10: ['Time', 'Čas'],
  11: ['Date', 'Datum'],
  12: ['Unsigned Int (0-4294967295)', 'Nepredznačeno celo število (0-4294967295)'],
  13: ['Signed Int (4-byte)', 'Predznačeno celo število (4-bajtno)'],
  14: ['Float (4-byte)', 'Decimalno število (4-bajtno)'],
  16: ['String (14 characters)', 'Besedilo (14 znakov)'],
  17: ['Scene Number', 'Številka scene'],
  18: ['Scene Control', 'Krmiljenje scene'],
  19: ['DateTime', 'Datum in čas'],
  20: ['Enum (HVAC Mode)', 'Naštevilka (način HVAC)'],
  232: ['RGB Color (3-byte)', 'Barva RGB (3-bajtna)'],
};

function getDptDescription(dpt: number | undefined): string {
  if (!dpt) return '';
  const pair = DPT_DESCRIPTIONS[dpt];
  return pair ? tt(...pair) : '';
}

// Computed-like: the DPT to display in the UI (always from mapping definition)

watch(
  () => props.modelValue,
  (newVal) => {
    console.log('[KNXReferenceInput] watch modelValue triggered:', {
      newVal,
      localConfigBefore: { ...localConfig.value },
      addressesAvailable: addresses.value.length,
      newValGroupAddress: newVal?.groupAddress,
      newValDpt: newVal?.dpt,
    });

    if (newVal) {
      localConfig.value = { ...newVal };
      // Find the address to get its name
      const addr = addresses.value.find((a) => a.groupAddress === newVal.groupAddress);
      console.log('[KNXReferenceInput] Found address:', {
        searched: newVal.groupAddress,
        found: addr ? 'yes' : 'no',
        addressName: addr?.addressName,
        fullAddress: addr,
      });

      searchQuery.value = addr?.addressName
        ? `${newVal.groupAddress} - ${addr.addressName}`
        : newVal.groupAddress || '';

      console.log('[KNXReferenceInput] Set searchQuery to:', searchQuery.value);
    }
  },
  { deep: true },
);

// Handle clearing the search query (user deletes all text)
watch(
  () => searchQuery.value,
  (newQuery) => {
    // If searchQuery is empty and localConfig has an address, clear it
    if (!newQuery || newQuery.trim() === '') {
      if (localConfig.value.groupAddress) {
        console.log('[KNXReferenceInput] Clearing address due to empty searchQuery');
        localConfig.value = {
          groupAddress: '',
          dpt: localConfig.value.dpt,
        };
        emit('update:modelValue', localConfig.value);
      }
    }
  },
);

function updateValue() {
  emit('update:modelValue', { ...localConfig.value });
}

const filtered = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();

  // Filter by expected DPT type if mapping OR existing config has a DPT
  let results = addresses.value;
  // Use mapping if available, otherwise use localConfig.dpt if we have an existing address configured

  const expectedDpt = getExpectedDptNumber();
  if (expectedDpt) {
    console.log(
      '[KNXReferenceInput] Filtering by DPT:',
      expectedDpt,
      'from',
      props.mapping ? 'mapping' : 'existing config',
    );
    results = results.filter((a) => {
      const addrDpt = extractDptNumber(getAddressDptValue(a));
      return addrDpt === expectedDpt;
    });
    console.log('[KNXReferenceInput] Filtered results:', results.length, 'addresses');
  }

  // Then filter by search query
  if (!q) return results;

  return results.filter((a) => {
    const addressMatch = String(a.groupAddress).toLowerCase().includes(q);
    const nameMatch = String(a.addressName || '')
      .toLowerCase()
      .includes(q);
    const dptMatch = String(a.dpt || '')
      .toLowerCase()
      .includes(q);

    return addressMatch || nameMatch || dptMatch;
  });
});

// Check if DPT is compatible with expected mapping
function isCompatibleDpt(addressDpt: string | number | undefined): boolean {
  const expectedDpt = getExpectedDptNumber();
  if (!expectedDpt) return true;
  if (!addressDpt) return false;

  const addrDptNum = extractDptNumber(addressDpt);
  return addrDptNum === expectedDpt;
}

async function loadAddresses() {
  if (!props.integrationId) return;
  if (addresses.value.length > 0) return; // Already loaded

  loading.value = true;
  error.value = null;
  try {
    const data = await apiFetch<{ knx?: KnxAddressRow[] }>(`/integrations/${props.integrationId}/datapoints`);

    const rawAddresses: KnxAddressRow[] = Array.isArray(data?.knx) ? data.knx : [];

    console.log('[KNXReferenceInput] Raw API response:', {
      count: rawAddresses.length,
      first5: rawAddresses.slice(0, 5).map((a) => ({
        groupAddress: a.groupAddress,
        addressName: a.addressName,
        dpt: a.dpt,
        allKeys: Object.keys(a),
      })),
    });

    // Filter for empty groupAddress and issue warnings
    const emptyAddresses = rawAddresses.filter((a) => !a.groupAddress || String(a.groupAddress).trim() === '');
    if (emptyAddresses.length > 0) {
      console.error('[KNXReferenceInput] WARNING: Found', emptyAddresses.length, 'addresses with empty groupAddress:');
      emptyAddresses.slice(0, 5).forEach((a) => {
        console.error('[KNXReferenceInput]  - addressName:', a.addressName, 'dpt:', a.dpt, 'full:', a);
      });
    }

    // Only use addresses with valid groupAddress
    addresses.value = rawAddresses.filter((a) => a.groupAddress && String(a.groupAddress).trim() !== '');

    console.log(
      '[KNXReferenceInput] Loaded',
      addresses.value.length,
      'valid KNX addresses (filtered',
      emptyAddresses.length,
      'empty), for integration',
      props.integrationId,
    );
    if (addresses.value.length > 0) {
      console.log('[KNXReferenceInput] First address sample:', JSON.stringify(addresses.value[0], null, 2));
    }
  } catch (__caught: unknown) {
    const e = __caught as Error;
    error.value = e.message || tt('Failed to load addresses', 'Naslovov ni bilo mogoče naložiti');
    console.error('[KNXReferenceInput] Failed to load addresses:', e);
  } finally {
    loading.value = false;
  }
}

// Load addresses when integration ID is available
watch(
  () => props.integrationId,
  (newId) => {
    if (newId && addresses.value.length === 0) {
      console.log('[KNXReferenceInput] Integration ID changed to:', newId);
      loadAddresses();
    }
  },
  { immediate: true },
);

function onSearchInput() {
  showDropdown.value = true;
  highlightedIndex.value = 0;
  // Just show dropdown - don't modify the actual address during search
  // The address is only updated when user selects from dropdown via selectAddress()
}

async function onSearchFocus() {
  await loadAddresses();
  console.log(
    '[KNXReferenceInput] onSearchFocus - Total addresses:',
    addresses.value.length,
    'Filtered:',
    filtered.value.length,
  );
  console.log('[KNXReferenceInput] Mapping:', props.mapping);
  if (addresses.value.length > 0) {
    console.log('[KNXReferenceInput] Sample address:', JSON.stringify(addresses.value[0], null, 2));
  }

  // Always show dropdown if we have any addresses loaded
  // The filtered computed will handle the DPT filtering
  if (addresses.value.length > 0) {
    showDropdown.value = true;
  }
}

function onSearchBlur() {
  // Delay to allow click events on dropdown items
  setTimeout(() => {
    showDropdown.value = false;
  }, 200);
}

function closeDropdown() {
  showDropdown.value = false;
  searchInput.value?.blur();
}

function navigateDown() {
  if (highlightedIndex.value < filtered.value.length - 1) {
    highlightedIndex.value++;
  }
}

function navigateUp() {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--;
  }
}

function selectFirstMatch() {
  if (filtered.value.length > 0) {
    selectAddress(filtered.value[highlightedIndex.value]);
  }
}

function selectAddress(addr: KnxAddressRow) {
  console.log('[KNXReferenceInput] selectAddress called with:', {
    groupAddress: String(addr.groupAddress),
    addressName: addr.addressName,
    dpt: addr.dpt,
    dptType: addr.dptType,
    full: JSON.stringify(addr),
  });

  // Validate that we have a valid group address
  if (!addr.groupAddress || String(addr.groupAddress).trim() === '') {
    console.warn('[KNXReferenceInput] Address has empty groupAddress!', addr);
    error.value = tt('Address has no group address configured', 'Naslov nima nastavljenega skupinskega naslova');
    return;
  }

  const expectedDpt = getExpectedDptNumber();
  localConfig.value = {
    groupAddress: String(addr.groupAddress),
    dpt: expectedDpt ?? extractDptNumber(getAddressDptValue(addr)),
  };
  searchQuery.value = addr.addressName ? `${addr.groupAddress} - ${addr.addressName}` : String(addr.groupAddress);
  console.log('[KNXReferenceInput] Set localConfig and searchQuery:', {
    localConfig: localConfig.value,
    searchQuery: searchQuery.value,
  });
  updateValue();
  showDropdown.value = false;
  highlightedIndex.value = 0;
}

// Load addresses on mount if integration ID is available
onMounted(async () => {
  console.log('[KNXReferenceInput] onMounted called:', {
    integrationId: props.integrationId,
    modelValue: props.modelValue,
    localConfig: localConfig.value,
    searchQuery: searchQuery.value,
  });

  if (props.integrationId) {
    await loadAddresses();
    // Update searchQuery with full name if address exists
    if (localConfig.value.groupAddress) {
      const addr = addresses.value.find((a) => a.groupAddress === localConfig.value.groupAddress);
      console.log('[KNXReferenceInput] onMounted - looking up address:', {
        groupAddress: localConfig.value.groupAddress,
        found: addr ? 'yes' : 'no',
        addressName: addr?.addressName,
      });

      searchQuery.value = addr?.addressName
        ? `${localConfig.value.groupAddress} - ${addr.addressName}`
        : localConfig.value.groupAddress;

      console.log('[KNXReferenceInput] onMounted - set searchQuery to:', searchQuery.value);
    }
  }
});
</script>
